import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { locationService, CreateSubcountyData, CreateWardData, CreateZoneData, LocationFilters } from '../services';

// County-wide Subcounties
export const useAllSubcounties = (filters?: LocationFilters) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['subcounties', 'all', filters],
    queryFn: async () => {
      const response = await locationService.getAllSubcounties(filters);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string }) => locationService.createCountySubcounty(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcounties'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string } }) =>
      locationService.updateCountySubcounty(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcounties'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => locationService.deleteCountySubcounty(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcounties'] });
    },
  });

  return {
    subcounties: data,
    isLoading,
    error,
    createSubcounty: createMutation.mutateAsync,
    updateSubcounty: updateMutation.mutateAsync,
    deleteSubcounty: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
};

// Module-specific Subcounties
export const useSubcounties = (moduleId: number, page?: number) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['subcounties', moduleId, page],
    queryFn: async () => {
      const response = await locationService.getSubcounties(moduleId, page);
      return response.data;
    },
    enabled: !!moduleId,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateSubcountyData) => locationService.createSubcounty(moduleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcounties', moduleId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateSubcountyData> }) =>
      locationService.updateSubcounty(moduleId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcounties', moduleId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => locationService.deleteSubcounty(moduleId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcounties', moduleId] });
    },
  });

  return {
    subcounties: data,
    isLoading,
    error,
    createSubcounty: createMutation.mutateAsync,
    updateSubcounty: updateMutation.mutateAsync,
    deleteSubcounty: deleteMutation.mutateAsync,
  };
};

// County-wide Wards
export const useAllWards = (filters?: LocationFilters & { subcounty_id?: string }) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['wards', 'all', filters],
    queryFn: async () => {
      const response = await locationService.getAllWards(filters);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; subcounty_id: number | string }) => locationService.createCountyWard(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wards'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name?: string; subcounty_id?: number | string } }) =>
      locationService.updateCountyWard(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wards'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => locationService.deleteCountyWard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wards'] });
    },
  });

  return {
    wards: data,
    isLoading,
    error,
    createWard: createMutation.mutateAsync,
    updateWard: updateMutation.mutateAsync,
    deleteWard: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
};

// Module-specific Wards
export const useWards = (moduleId: number, page?: number) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['wards', moduleId, page],
    queryFn: async () => {
      const response = await locationService.getWards(moduleId, page);
      return response.data;
    },
    enabled: !!moduleId,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateWardData) => locationService.createWard(moduleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wards', moduleId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateWardData> }) =>
      locationService.updateWard(moduleId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wards', moduleId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => locationService.deleteWard(moduleId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wards', moduleId] });
    },
  });

  return {
    wards: data,
    isLoading,
    error,
    createWard: createMutation.mutateAsync,
    updateWard: updateMutation.mutateAsync,
    deleteWard: deleteMutation.mutateAsync,
  };
};

// County-wide Zones
export const useAllZones = (filters?: LocationFilters & { ward_id?: string }) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['zones', 'all', filters],
    queryFn: async () => {
      const response = await locationService.getAllZones(filters);
      return response.data;
    },
  });

  return {
    zones: data,
    isLoading,
    error,
    refetch,
  };
};

// Module-specific Zones
export const useZones = (moduleId: number, page?: number) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['zones', moduleId, page],
    queryFn: async () => {
      const response = await locationService.getZones(moduleId, page);
      return response.data;
    },
    enabled: !!moduleId,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateZoneData) => locationService.createZone(moduleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones', moduleId] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateZoneData> }) =>
      locationService.updateZone(moduleId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones', moduleId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => locationService.deleteZone(moduleId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones', moduleId] });
    },
  });

  return {
    zones: data,
    isLoading,
    error,
    createZone: createMutation.mutateAsync,
    updateZone: updateMutation.mutateAsync,
    deleteZone: deleteMutation.mutateAsync,
  };
};
